import { routeCarrier } from "@/setup/routes/catalog.routes";
import { ErrorModal } from "@dexteel/mesf-core";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
	Autocomplete,
	CircularProgress,
	IconButton,
	TextField,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { FieldError } from "react-hook-form";
import { Link } from "react-router-dom";
import { Carrier } from "../models/Carrier";
import { getCarriers } from "../repositories/CarrierRepository";

type Props = {
	id?: string;
	label: string;
	value: number | null;
	onChange?: Function;
	fieldError?: FieldError | undefined;
	disabled?: boolean;
	required?: boolean;
};

const useCarriers = (partId?: number, onError?: (error: Error) => void) => {
	return useQuery<Carrier[], Error>({
		queryKey: ["carriers", partId],
		queryFn: ({ signal }) => getCarriers({ partId }, signal),
		onError: onError,
	});
};

export const CarrierSelector = ({
	id,
	label,
	value,
	onChange = () => {},
	fieldError,
	disabled = false,
	required = false,
}: Props) => {
	const [error, setError] = useState("");

	const { data: carriers, isLoading } = useCarriers(undefined, (error) =>
		setError(error.message),
	);

	const selectedChock =
		carriers?.find((chock) => chock.PartId === value) || null;

	return (
		<>
			<Autocomplete
				id={id}
				clearOnEscape
				autoHighlight
				options={carriers || []}
				getOptionLabel={(option) => option.PartName}
				isOptionEqualToValue={(option, value) => option.PartId === value.PartId}
				disabled={disabled}
				renderInput={(params) => (
					<TextField
						{...params}
						label={label}
						variant="outlined"
						fullWidth
						size="small"
						error={!!fieldError}
						helperText={fieldError?.message}
						required={required}
						InputProps={{
							...params.InputProps,
							endAdornment: (
								<>
									{isLoading ? (
										<CircularProgress color="inherit" size={20} />
									) : (
										<>
											{params.InputProps.endAdornment}
											{value ? (
												<IconButton
													component={Link}
													to={`${routeCarrier}/${value}`}
													target="_blank"
													size="small"
													title="See in cassette chocks"
												>
													<VisibilityIcon fontSize="small" />
												</IconButton>
											) : null}
										</>
									)}
								</>
							),
						}}
					/>
				)}
				value={selectedChock}
				onChange={(event, newValue) => {
					onChange(newValue?.PartId ?? null);
				}}
			/>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error loading cassettes"
			/>
		</>
	);
};
